import axios from 'axios'
import React, { useEffect, useRef, useState } from 'react'
import { api } from '../utils/Api'
import { Typography, Flex, Table, Button, Row, Col, Select } from 'antd'
import Layout from '../layout/Layout'
import { CustomSearch, CustomSpinner } from '../utils/CustomComponents'
import dayjs from 'dayjs'
import { CSVLink } from 'react-csv'

const { Text } = Typography

const MAX_LOADS = 8

const columnsCSV = [
    { key: 'date', label: 'Date' },
    { key: 'truck', label: 'Truck #' },
    { key: 'count', label: 'Trips' },
    { key: 'yards', label: 'Yards' },
    { key: 'time1', label: 'Load 1' },
    { key: 'diff1', label: '→ 1-2' },
    { key: 'time2', label: 'Load 2' },
    { key: 'diff2', label: '→ 2-3' },
    { key: 'time3', label: 'Load 3' },
    { key: 'diff3', label: '→ 3-4' },
    { key: 'time4', label: 'Load 4' },
    { key: 'diff4', label: '→ 4-5' },
    { key: 'time5', label: 'Load 5' },
    { key: 'diff5', label: '→ 5-6' },
    { key: 'time6', label: 'Load 6' },
    { key: 'diff6', label: '→ 6-7' },
    { key: 'time7', label: 'Load 7' },
    { key: 'diff7', label: '→ 7-8' },
    { key: 'time8', label: 'Load 8' },
]

const buildTimeColumns = () => {
    const cols = []
    for (let n = 1; n <= MAX_LOADS; n++) {
        cols.push({
            title: `Load ${n}`,
            dataIndex: `time${n}`,
            key: `time${n}`,
            width: 70,
            render: (val) => <Text style={{ fontSize: 12 }}>{val || '-'}</Text>,
        })
        if (n < MAX_LOADS) {
            cols.push({
                title: '→',
                dataIndex: `diff${n}`,
                key: `diff${n}`,
                width: 60,
                render: (val, record) => (
                    <Text style={{ fontSize: 12, color: record[`diff${n}Red`] ? '#b71c1c' : '#888', backgroundColor: record[`diff${n}Red`] ? '#ffcdd2' : undefined }}>
                        {val || ''}
                    </Text>
                ),
            })
        }
    }
    return cols
}

const RoundsPerTruck = () => {
    const [loading, setLoading] = useState(true)
    const [startDate, setStartDate] = useState(dayjs())
    const [endDate, setEndDate] = useState(dayjs())
    const [plant, setPlant] = useState(null)
    const [list, setList] = useState([])
    const [listCSV, setListCSV] = useState([])
    const refDownload = useRef()

    const plantOptions = [
        { value: 'San Benito',  label: 'San Benito' },
        { value: 'Brownsville', label: 'Brownsville' },
        { value: 'Mission',     label: 'Mission' },
    ]

    const columns = [
        {
            title: 'Date',
            dataIndex: 'date',
            key: 'date',
            fixed: 'left',
            width: 100,
            render: (val) => <Text style={{ fontSize: 12 }}>{val}</Text>,
        },
        {
            title: 'Truck #',
            dataIndex: 'truck',
            key: 'truck',
            fixed: 'left',
            width: 80,
            render: (val) => <Text style={{ fontSize: 12 }}>{val}</Text>,
        },
        {
            title: 'Trips',
            dataIndex: 'count',
            key: 'count',
            width: 60,
            render: (val) => <Text style={{ fontSize: 12 }}>{val}</Text>,
        },
        {
            title: 'Yards',
            dataIndex: 'yards',
            key: 'yards',
            width: 70,
            render: (val) => <Text style={{ fontSize: 12 }}>{val}</Text>,
        },
        ...buildTimeColumns(),
    ]

    useEffect(() => {
        getData()
    }, [startDate, endDate, plant])

    const getData = async () => {
        setLoading(true)
        const start = `${startDate.year()}-${startDate.month() + 1}-${startDate.date()}`
        const end = `${endDate.year()}-${endDate.month() + 1}-${endDate.date()}`
        const plantParam = plant ? `&plant=${encodeURIComponent(plant)}` : ''
        const url = api + `/loadroundspertruck?startDate=${start}&endDate=${end}${plantParam}`
        const token = localStorage.getItem('token')
        axios.defaults.headers.common = { Authorization: `Bearer ${token}` }

        try {
            const { data } = await axios.get(url)
            const rows = data.map((item) => {
                const loads = (item.loads || [])
                    .slice()
                    .sort((a, b) => {
                        const ta = new Date(a.timeToJob || a.createdAt).getTime()
                        const tb = new Date(b.timeToJob || b.createdAt).getTime()
                        return ta - tb
                    })
                    .slice(0, MAX_LOADS)

                const row = {
                    key: `${item._id.year}-${item._id.month}-${item._id.day}-${item._id.truck}`,
                    date: `${String(item._id.month).padStart(2, '0')}/${String(item._id.day).padStart(2, '0')}/${String(item._id.year).slice(-2)}`,
                    truck: item._id.truck || '-',
                    count: item.count,
                    yards: item.totalYards,
                }

                loads.forEach((load, idx) => {
                    const n = idx + 1
                    const t = load.timeToJob || load.createdAt
                    row[`time${n}`] = t ? dayjs(t).format('HH:mm') : '-'

                    if (idx < loads.length - 1) {
                        const tNext = loads[idx + 1].timeToJob || loads[idx + 1].createdAt
                        if (t && tNext) {
                            const diffMin = dayjs(tNext).diff(dayjs(t), 'minute')
                            const h = Math.floor(diffMin / 60)
                            const m = diffMin % 60
                            row[`diff${n}`] = `${h}:${String(m).padStart(2, '0')}`
                            if (diffMin >= 8 * 60) row[`diff${n}Red`] = true
                        } else {
                            row[`diff${n}`] = '-'
                        }
                    }
                })

                return row
            })

            setList(rows)
            setListCSV(rows)
            setLoading(false)
        } catch (err) {
            console.error(err.message)
            setLoading(false)
        }
    }

    const onRangeChange = (dates) => {
        if (dates) {
            setStartDate(dates[0])
            setEndDate(dates[1])
        }
    }

    return (
        <Layout title="Rounds per Truck" description="Logistics Operations" type="medium">
            <Flex vertical gap="large">
                <CustomSearch
                    loading={loading}
                    startDate={startDate}
                    endDate={endDate}
                    dateFormat="MM/DD/YYYY"
                    onRangeChange={onRangeChange}
                    labelPicker="Plant"
                    optionsPicker={plantOptions}
                    onChangePicker={setPlant}
                    pickerValue={plant}
                    allowClearPicker={true}
                />
                <Row gutter={16}>
                    <Col span={24}>
                        <CSVLink
                            data={listCSV}
                            headers={columnsCSV}
                            filename="57concrete-rounds-per-truck.csv"
                            hidden
                            ref={refDownload}
                        >
                            Download report
                        </CSVLink>
                        <Button
                            type="primary"
                            style={{ width: '100%' }}
                            onClick={() => refDownload.current.link.click()}
                            loading={loading}
                        >
                            Download Report
                        </Button>
                    </Col>
                </Row>
                {loading ? (
                    <CustomSpinner />
                ) : (
                    <Table
                        columns={columns}
                        dataSource={list}
                        pagination={{
                            position: ['bottomRight'],
                            pageSizeOptions: [10, 20, 50, 100, 200],
                            defaultPageSize: 50,
                            showSizeChanger: true,
                        }}
                        scroll={{ x: true }}
                        style={{ minWidth: '100%' }}
                        size="small"
                    />
                )}
            </Flex>
        </Layout>
    )
}

export default RoundsPerTruck
